import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { Modal, Button, Spin } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { ThemeContext } from '../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { useAppSelector } from '../../store/hooks'
import { selectUserIdSlice } from '../../store/user-id-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { rechargeTypeTracking } from './services/rechargeTypeTracking'

import IntlMessages from '../../util/IntlMessages'
import { getRechargeTypeInfo, getRechargeTypeDisplayName } from '../../util/recharge-type-info'
import { useGameColleagues, useSaveGameResult, useGameLeaderboard } from '../../services/api/recharge-type-hooks'

import { GameColleague, RechargeType } from '@vacationtracker/shared/types/recharge-type'

import './styles.less'

interface RechargeTypeGameProps {
  visible: boolean
  onClose: () => void
  onShowLeaderboard?: () => void
  requiredNumberOfUsers?: number
}

export enum GameState {
  LOADING = 'loading',
  START = 'start',
  PLAYING = 'playing',
  CORRECT_FEEDBACK = 'correct-feedback',
  WRONG_FEEDBACK = 'wrong-feedback', // Show correct answer flash before game over
  GAME_OVER = 'game-over',
  NO_COLLEAGUES = 'no-colleagues',
}
export enum ActiveTab {
  PLAY = 'play',
  LEADERBOARD = 'leaderboard',
}

const RECHARGE_TYPE_OPTIONS = [RechargeType.SOCIAL_SPRINTER, RechargeType.STRUCTURED_SOCIALIZER, RechargeType.SPONTANEOUS_SOLOIST, RechargeType.DEEP_RESTORER]

// Auto-advance delay after correct answer (per spec: 1.2 seconds)
const CORRECT_FEEDBACK_DELAY = 1200

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

/**
 * Guess the Type Game
 * Per spec: "The game uses a streak-based scoring system. There are no points."
 * - Correct answer: green highlight, confetti, auto-advance after 1.2s
 * - Wrong answer: red highlight, game over (correct answer NOT revealed per spec)
 */
export const RechargeTypeGame: React.FC<RechargeTypeGameProps> = ({ visible, onClose, onShowLeaderboard, requiredNumberOfUsers = 10 }) => {
  const { formatMessage } = useIntl()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === DARK_THEME
  const { userId } = useAppSelector(selectUserIdSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const autoAdvanceTimer = useRef<NodeJS.Timeout | null>(null)

  // TanStack Query hooks
  const {
    data: colleaguesData,
    isLoading: isLoadingColleagues,
    refetch: refetchColleagues,
  } = useGameColleagues(10, {
    enabled: visible, // Only fetch when modal is visible
  })
  const { data: leaderboardData, refetch: refetchLeaderboard } = useGameLeaderboard(10, {
    enabled: visible, // Only fetch when modal is visible
  })
  const { mutate: saveGameResultMutation } = useSaveGameResult({
    onSuccess: () => {
      // Refetch leaderboard after a short delay to ensure backend has processed
      setTimeout(() => {
        void refetchLeaderboard().then((result) => {
          if (result.data?.userStats?.bestStreak !== undefined) {
            const newBestStreak = result.data.userStats.bestStreak
            // Only update if the new value is higher than current (prevents race conditions)
            setBestStreak((prev) => Math.max(prev, newBestStreak))
          }
        })
      }, 500) // Small delay to ensure backend has processed
    },
    onError: () => {
      setSaveError(true)
    },
  })

  const [gameState, setGameState] = useState<GameState>(GameState.LOADING)
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.PLAY)
  const [colleagues, setColleagues] = useState<GameColleague[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentStreak, setCurrentStreak] = useState(0)
  const [bestStreak, setBestStreak] = useState(0)
  const [isNewRecord, setIsNewRecord] = useState(false)
  const [selectedAnswer, setSelectedAnswer] = useState<RechargeType | null>(null)
  const [saveError, setSaveError] = useState(false)
  const [colleagueCount, setColleagueCount] = useState(0)
  const [gameWon, setGameWon] = useState(false) // Track if game was won (all colleagues guessed correctly)
  const [isPlayingAgain, setIsPlayingAgain] = useState(false) // Flag to prevent useEffect from running during play again

  // Shuffle options for each question to prevent pattern memorization
  const shuffledOptions = useMemo(() => {
    return shuffleArray(RECHARGE_TYPE_OPTIONS)
  }, [currentIndex])

  const currentColleague = colleagues[currentIndex]

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) {
        clearTimeout(autoAdvanceTimer.current)
      }
    }
  }, [])

  // Reset game when modal closes
  useEffect(() => {
    if (!visible) {
      resetGame()
    }
  }, [visible])

  // Load game data when modal opens (only on initial load)
  useEffect(() => {
    if (!visible) {
      return
    }

    // Skip this effect during play again flow - handlePlayAgain manages its own state
    if (isPlayingAgain) {
      return
    }

    // Only initialize on first load (when gameState is LOADING)
    if (gameState !== GameState.LOADING) {
      return
    }

    if (isLoadingColleagues) {
      return // Keep LOADING state
    }

    if (colleaguesData) {
      const { colleagues } = colleaguesData
      // Show actual number of colleagues in the game (limited to 10), not total company count
      setColleagueCount(colleagues.length)

      // Set user's best streak from stats (defaults to 0 if no games played)
      // Only set on initial load when leaderboardData is available
      if (leaderboardData?.userStats?.bestStreak !== undefined) {
        setBestStreak(leaderboardData.userStats.bestStreak)
      } else {
        // Explicitly set to 0 if no stats available yet
        setBestStreak(0)
      }

      if (colleagues.length === 0) {
        setGameState(GameState.NO_COLLEAGUES)
      } else {
        setColleagues(colleagues)
        setGameState(GameState.START)
      }
    }
  }, [visible, isLoadingColleagues, colleaguesData, leaderboardData, gameState, isPlayingAgain]) // Keep leaderboardData for initial load

  const resetGame = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current)
    }
    setGameState(GameState.LOADING)
    setActiveTab(ActiveTab.PLAY)
    setColleagues([])
    setCurrentIndex(0)
    setCurrentStreak(0)
    setIsNewRecord(false)
    setSelectedAnswer(null)
    setSaveError(false)
    setGameWon(false)
    setIsPlayingAgain(false)
  }

  const handleStartGame = () => {
    if (userId && authCompany?.id) {
      rechargeTypeTracking.trackGameStarted({
        userId,
        companyId: authCompany.id,
      })
    }
    // Ensure we have colleagues before starting
    if (colleagues.length === 0) {
      console.error('Cannot start game: no colleagues available')
      return
    }
    setCurrentIndex(0)
    setSelectedAnswer(null)
    setGameState(GameState.PLAYING)
  }

  const handleSelectAnswer = (answer: RechargeType) => {
    if (selectedAnswer !== null || !currentColleague) return

    setSelectedAnswer(answer)
    const isCorrect = answer === currentColleague.rechargeType
    const newStreak = isCorrect ? currentStreak + 1 : currentStreak

    // Track guess made
    if (userId && authCompany?.id) {
      rechargeTypeTracking.trackGameGuessMade({
        userId,
        companyId: authCompany.id,
        guessIsCorrect: isCorrect,
        currentStreak: newStreak,
      })
    }

    if (isCorrect) {
      // Correct: increment streak, show feedback, auto-advance
      setCurrentStreak(newStreak)
      setGameState(GameState.CORRECT_FEEDBACK)

      // Auto-advance to next colleague after delay (per spec: 1.2s)
      // Pass newStreak to avoid stale closure issue when game ends
      autoAdvanceTimer.current = setTimeout(() => {
        advanceToNextColleague(newStreak)
      }, CORRECT_FEEDBACK_DELAY)
    } else {
      // Wrong: show correct answer flash, then game over
      setGameState(GameState.WRONG_FEEDBACK)

      // Flash correct answer for 1.5 seconds, then end game
      autoAdvanceTimer.current = setTimeout(() => {
        endGame(currentStreak, false)
      }, 1500) // 1 second to see the correct answer
    }
  }

  const advanceToNextColleague = (updatedStreak?: number) => {
    // Use updatedStreak if provided (to avoid stale closure), otherwise use currentStreak
    const streakToUse = updatedStreak !== undefined ? updatedStreak : currentStreak

    if (currentIndex < colleagues.length - 1) {
      setCurrentIndex((prev) => prev + 1)
      setSelectedAnswer(null)
      setGameState(GameState.PLAYING)
    } else {
      // Ran out of colleagues - end game with perfect streak
      // Use the updated streak value to ensure correct final score
      endGame(streakToUse, true)
    }
  }

  const endGame = (finalStreak: number, guessIsCorrect: boolean) => {
    // Track if game was won (all colleagues guessed correctly)
    setGameWon(guessIsCorrect)

    // Track game ended
    if (userId && authCompany?.id) {
      rechargeTypeTracking.trackGameEnded({
        userId,
        companyId: authCompany.id,
        guessIsCorrect,
        currentStreak: finalStreak,
      })
    }

    // Check if this is a new personal best
    const isNewPersonalBest = finalStreak > bestStreak
    setIsNewRecord(isNewPersonalBest)

    if (isNewPersonalBest) {
      setBestStreak(finalStreak)
    }

    // Set game over state FIRST - this prevents useEffect from resetting the game
    setGameState(GameState.GAME_OVER)

    // Save game result using mutation (don't refetch leaderboard to avoid resetting game)
    setSaveError(false)
    saveGameResultMutation({ streak: finalStreak })
  }

  const handlePlayAgain = () => {
    if (userId && authCompany?.id) {
      // track play again clicked
      rechargeTypeTracking.trackGamePlayAgainClicked({
        userId,
        companyId: authCompany.id,
      })
    }
    // Reset game state - set to LOADING immediately to prevent showing stale game over screen
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current)
    }

    // Set flag to prevent useEffect from interfering
    setIsPlayingAgain(true)

    // Set loading state FIRST to prevent flash of wrong game over screen
    setGameState(GameState.LOADING)
    setCurrentIndex(0)
    setCurrentStreak(0)
    setIsNewRecord(false)
    setSelectedAnswer(null)
    setSaveError(false)
    setGameWon(false)
    setActiveTab(ActiveTab.PLAY)

    // Refetch data using TanStack Query
    void refetchColleagues().then((result) => {
      // Once colleagues are refetched, start the game directly (skip start screen)
      const fetchedColleagues = result.data?.colleagues
      if (fetchedColleagues && fetchedColleagues.length > 0) {
        setColleagues(fetchedColleagues)
        setColleagueCount(fetchedColleagues.length)
        // Refetch leaderboard and update best streak
        void refetchLeaderboard().then((leaderboardResult) => {
          if (leaderboardResult.data?.userStats?.bestStreak !== undefined) {
            setBestStreak(leaderboardResult.data.userStats.bestStreak)
          } else {
            setBestStreak(0)
          }
          // Clear the flag after all data is loaded
          setIsPlayingAgain(false)
        })
        setGameState(GameState.PLAYING)
      } else {
        // If no colleagues, show no colleagues screen
        setIsPlayingAgain(false)
        setGameState(GameState.NO_COLLEAGUES)
      }
    })
  }

  const handleClose = () => {
    if (userId && authCompany?.id) {
      // track game dismissed
      rechargeTypeTracking.trackGameDismissed({
        userId,
        companyId: authCompany.id,
      })
    }
    resetGame()
    onClose()
  }

  const handleShowLeaderboard = () => {
    if (!onShowLeaderboard) {
      return
    }

    resetGame()
    onClose()
    onShowLeaderboard()
  }

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab)
    if (tab === ActiveTab.LEADERBOARD && onShowLeaderboard) {
      handleShowLeaderboard()
    }
  }

  // Get tagline for each recharge type
  const getTypeTagline = (type: RechargeType): string => {
    const taglines: Record<RechargeType, string> = {
      [RechargeType.SOCIAL_SPRINTER]: formatMessage({ id: 'rechargeType.game.tagline.socialSprinter' }),
      [RechargeType.STRUCTURED_SOCIALIZER]: formatMessage({ id: 'rechargeType.game.tagline.structuredSocializer' }),
      [RechargeType.SPONTANEOUS_SOLOIST]: formatMessage({ id: 'rechargeType.game.tagline.spontaneousSoloist' }),
      [RechargeType.DEEP_RESTORER]: formatMessage({ id: 'rechargeType.game.tagline.deepRestorer' }),
    }
    return taglines[type]
  }

  const renderHeader = () => (
    <div className="game-modal-header">
      <div className="header-title">
        <span className="header-emoji">🎯</span>
        <span className="header-text">
          <IntlMessages id="rechargeType.game.title" />
        </span>
      </div>
      <button className="header-close" onClick={handleClose}>
        <CloseOutlined />
      </button>
    </div>
  )

  const renderStreakBar = () => (
    <div className="game-streak-bar">
      <div className="streak-left">
        <span className="streak-label">
          <IntlMessages id="rechargeType.game.currentStreak" />
        </span>
        <div className="streak-value">
          <span className="streak-fire">🔥</span>
          <span className="streak-number">{currentStreak}</span>
        </div>
      </div>
      <div className="streak-right">
        <span className="streak-label">
          <IntlMessages id="rechargeType.game.yourBest" />
        </span>
        <span className="streak-best">{bestStreak}</span>
      </div>
    </div>
  )

  const renderTabBar = () => (
    <div className="game-tab-bar">
      <button className={`tab-item ${activeTab === ActiveTab.PLAY ? 'active' : ''}`} onClick={() => handleTabChange(ActiveTab.PLAY)}>
        <span className="tab-icon">🎮</span>
        <span className="tab-label">
          <IntlMessages id="rechargeType.game.tabPlay" />
        </span>
      </button>
      <button className={`tab-item ${activeTab === ActiveTab.LEADERBOARD ? 'active' : ''}`} onClick={() => handleTabChange(ActiveTab.LEADERBOARD)}>
        <span className="tab-icon">🏆</span>
        <span className="tab-label">
          <IntlMessages id="rechargeType.game.tabLeaderboard" />
        </span>
      </button>
    </div>
  )

  const renderStartScreen = () => (
    <div className="game-content game-start">
      <div className="start-icon">🎮</div>
      <h2 className="start-title">
        <IntlMessages id="rechargeType.game.description" />
      </h2>
      <Button type="primary" size="large" onClick={handleStartGame} className="start-btn">
        <IntlMessages id="rechargeType.game.start" />
      </Button>
      <div className="start-info">
        <span className="info-check">✓</span>
        <span className="info-text">{formatMessage({ id: 'rechargeType.game.colleaguesCount' }, { count: colleagueCount })}</span>
      </div>
    </div>
  )

  const renderPlayingScreen = () => {
    if (!currentColleague || colleagues.length === 0) {
      console.warn('Cannot render playing screen: no current colleague or empty colleagues array')
      return null
    }

    return (
      <div className="game-content game-playing">
        <div className="playing-header">
          <span className="playing-label">
            <IntlMessages id="rechargeType.game.question" />
          </span>
          <h3 className="playing-question">
            <IntlMessages id="rechargeType.game.canYouGuess" />
          </h3>
        </div>
        <div className="colleague-card">
          <UserAvatar id={currentColleague.userId} name={currentColleague.name} avatar={currentColleague.imageUrl} avatarSize={80} shape="circle" />
          <h3 className="colleague-name">{currentColleague.name}</h3>
          {currentColleague.department && <span className="colleague-department">{currentColleague.department}</span>}
        </div>
        <div className="answer-grid">
          {shuffledOptions.map((type) => {
            const info = getRechargeTypeInfo(type, formatMessage)
            const displayName = getRechargeTypeDisplayName(type, formatMessage)

            return (
              <button
                key={type}
                className="answer-card"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSelectAnswer(type)
                }}
                disabled={selectedAnswer !== null || !currentColleague}
                type="button"
              >
                <span className="answer-emoji">{info.emoji}</span>
                <span className="answer-name">{displayName}</span>
                <span className="answer-tagline">{getTypeTagline(type)}</span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderCorrectFeedbackScreen = () => {
    if (!currentColleague) return null

    return (
      <div className="game-content game-feedback correct">
        <div className="confetti-container">
          {/* Confetti dots */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`confetti confetti-${i % 5}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            />
          ))}
        </div>
        <div className="playing-header">
          <span className="playing-label">
            <IntlMessages id="rechargeType.game.correct" />
          </span>
          <h3 className="playing-question">{formatMessage({ id: 'rechargeType.game.streakCount' }, { count: currentStreak })}</h3>
        </div>
        <div className="colleague-card">
          <UserAvatar id={currentColleague.userId} name={currentColleague.name} avatar={currentColleague.imageUrl} avatarSize={80} shape="circle" />
          <h3 className="colleague-name">{currentColleague.name}</h3>
        </div>
        <div className="answer-grid">
          {shuffledOptions.map((type) => {
            const info = getRechargeTypeInfo(type, formatMessage)
            const displayName = getRechargeTypeDisplayName(type, formatMessage)
            const isSelected = selectedAnswer === type
            const isCorrectAnswer = type === currentColleague.rechargeType

            return (
              <div key={type} className={`answer-card ${isSelected ? 'selected correct' : ''} ${isCorrectAnswer && !isSelected ? 'correct-answer' : ''}`}>
                <span className="answer-emoji">{info.emoji}</span>
                <span className="answer-name">{displayName}</span>
                <span className="answer-tagline">{getTypeTagline(type)}</span>
              </div>
            )
          })}
        </div>
        <p className="auto-advance-hint">
          <IntlMessages id="rechargeType.game.autoAdvance" />
        </p>
      </div>
    )
  }

  const renderWrongFeedbackScreen = () => {
    if (!currentColleague) return null

    const correctType = currentColleague.rechargeType
    const correctInfo = getRechargeTypeInfo(correctType, formatMessage)
    const correctDisplayName = getRechargeTypeDisplayName(correctType, formatMessage)

    return (
      <div className="game-content game-feedback wrong-flash">
        <div className="wrong-feedback-header">
          <div className="wrong-feedback-emoji">😅</div>
          <h2 className="wrong-feedback-title">
            <IntlMessages id="rechargeType.game.wrong" />
          </h2>
        </div>
        <div className="colleague-card">
          <UserAvatar id={currentColleague.userId} name={currentColleague.name} avatar={currentColleague.imageUrl} avatarSize={80} shape="circle" />
          <h3 className="colleague-name">{currentColleague.name}</h3>
        </div>
        <div className="correct-answer-flash">
          <div className="flash-label">
            <IntlMessages id="rechargeType.game.correctAnswer" />
          </div>
          <div className="answer-card correct-answer-highlight">
            <span className="answer-emoji">{correctInfo.emoji}</span>
            <span className="answer-name">{correctDisplayName}</span>
            <span className="answer-tagline">{getTypeTagline(correctType)}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderGameOverScreen = () => {
    // Show win screen if game was won (all colleagues guessed correctly)
    if (gameWon) {
      return (
        <div className="game-content game-feedback correct-win">
          <div className="feedback-emoji">🎉</div>
          <h2 className="feedback-title">
            <IntlMessages id="rechargeType.game.perfect" />
          </h2>
          <p className="feedback-message">{formatMessage({ id: 'rechargeType.game.allCorrect' }, { count: colleagueCount })}</p>
          <div className="streak-card">
            <span className="streak-card-label">
              <IntlMessages id="rechargeType.game.yourStreak" />
            </span>
            <span className="streak-card-value">{currentStreak}</span>
            <span className="streak-card-desc">
              <IntlMessages id="rechargeType.game.correctInRow" />
            </span>
          </div>
          {isNewRecord && currentStreak > 0 && (
            <div className="new-record-badge">
              🏆 <IntlMessages id="rechargeType.game.newPersonalBest" />
            </div>
          )}
          {saveError && (
            <div className="save-error-warning">
              <IntlMessages id="rechargeType.game.scoreNotSaved" />
            </div>
          )}
          <div className="feedback-actions">
            <Button type="primary" size="large" onClick={handlePlayAgain} className="play-again-btn">
              <IntlMessages id="rechargeType.game.playAgain" />
            </Button>
            {onShowLeaderboard && (
              <Button size="large" onClick={handleShowLeaderboard} className="leaderboard-btn">
                <IntlMessages id="rechargeType.game.viewLeaderboard" />
              </Button>
            )}
          </div>
        </div>
      )
    }

    // Show loss screen (wrong guess)
    return (
      <div className="game-content game-feedback wrong">
        <div className="feedback-emoji">😅</div>
        <h2 className="feedback-title">
          <IntlMessages id="rechargeType.game.streakEnded" />
        </h2>
        <p className="feedback-message">{formatMessage({ id: 'rechargeType.game.wrongGuess' }, { name: currentColleague?.name.split(' ')[0] || '' })}</p>
        <div className="streak-card">
          <span className="streak-card-label">
            <IntlMessages id="rechargeType.game.yourStreak" />
          </span>
          <span className="streak-card-value">{currentStreak}</span>
          <span className="streak-card-desc">
            <IntlMessages id="rechargeType.game.correctInRow" />
          </span>
        </div>
        {isNewRecord && currentStreak > 0 && (
          <div className="new-record-badge">
            🏆 <IntlMessages id="rechargeType.game.newPersonalBest" />
          </div>
        )}
        {saveError && (
          <div className="save-error-warning">
            <IntlMessages id="rechargeType.game.scoreNotSaved" />
          </div>
        )}
        <div className="feedback-actions">
          <Button type="primary" size="large" onClick={handlePlayAgain} className="play-again-btn">
            <IntlMessages id="rechargeType.game.playAgain" />
          </Button>
          {onShowLeaderboard && (
            <Button size="large" onClick={handleShowLeaderboard} className="leaderboard-btn">
              <IntlMessages id="rechargeType.game.viewLeaderboard" />
            </Button>
          )}
        </div>
      </div>
    )
  }

  const renderNoColleaguesScreen = () => (
    <div className="game-content game-no-colleagues">
      <div className="no-colleagues-icon">😔</div>
      <h2 className="no-colleagues-title">
        <IntlMessages id="rechargeType.game.noColleagues" />
      </h2>
      <p className="no-colleagues-description">
        {requiredNumberOfUsers > 0
          ? formatMessage({ id: 'rechargeType.game.noColleaguesDescription' }, { count: requiredNumberOfUsers })
          : formatMessage({ id: 'rechargeType.game.noColleaguesDescriptionGeneric' })}
      </p>
      <Button type="primary" onClick={handleClose}>
        <IntlMessages id="app.close" />
      </Button>
    </div>
  )

  const renderContent = () => {
    if (gameState === 'loading') {
      return (
        <div className="game-loading">
          <Spin size="large" />
        </div>
      )
    }

    switch (gameState) {
      case 'start':
        return renderStartScreen()
      case 'playing':
        return renderPlayingScreen()
      case 'correct-feedback':
        return renderCorrectFeedbackScreen()
      case 'wrong-feedback':
        return renderWrongFeedbackScreen()
      case 'game-over':
        return renderGameOverScreen()
      case 'no-colleagues':
        return renderNoColleaguesScreen()
      default:
        return null
    }
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={480}
      centered
      closable={false}
      maskClosable={false}
      className={`recharge-game-modal-v2 ${isDark ? 'dark' : ''}`}
    >
      <div className="game-modal-container">
        {renderHeader()}
        {gameState !== 'loading' && gameState !== 'no-colleagues' && renderStreakBar()}
        {renderContent()}
        {gameState !== 'loading' && gameState !== 'no-colleagues' && renderTabBar()}
      </div>
    </Modal>
  )
}
